import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import client from "@/lib/glossyApi";
import { getFeatures } from "@/lib/features";
import { MessageCircle, X, ArrowRight } from "lucide-react";

export default function AskSme() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const sessionRef = useRef(Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => { getFeatures().then((f) => setVisible(f.concierge !== false)); }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const question = q.trim();
    if (!question || busy) return;
    setThread((t) => [...t, { role: "you", text: question }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await client.post("/ask", { question, session_id: sessionRef.current });
      setThread((t) => [...t, { role: "sme", text: data.answer, members: data.members }]);
    } catch {
      setThread((t) => [...t, { role: "sme", text: "The desk is briefly away — try me again in a moment." }]);
    }
    setBusy(false);
  };

  // The concierge lives where readers are actively looking to hire — the Directory and member pages.
  const onConciergePage = pathname === "/directory" || pathname.startsWith("/member/");
  if (!visible || !onConciergePage) return null;
  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-[60] bg-ink text-cream border border-gloss-gold/60 px-5 py-3.5 flex items-center gap-2 overline hover:bg-black transition-colors shadow-xl"
        data-testid="ask-sme-toggle"
      >
        {open ? <X size={15} /> : <MessageCircle size={15} />} Ask S/ME
      </button>
      {open && (
        <div className="fixed bottom-20 right-5 z-[60] w-[92vw] max-w-sm bg-ink text-cream border border-cream/20 shadow-2xl flex flex-col" style={{ height: "480px" }} data-testid="ask-sme-panel">
          <div className="px-5 py-4 border-b border-cream/15">
            <p className="overline text-gloss-gold">The Concierge</p>
            <p className="font-sans text-xs text-cream/60 mt-1">Looking for someone brilliant? Ask — we only recommend our members.</p>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {thread.length === 0 && (
              <p className="font-serif text-lg text-cream/70 leading-snug">Try: "Who can do my branding in East London?"</p>
            )}
            {thread.map((m, i) => (
              <div key={i} className={m.role === "you" ? "text-right" : ""}>
                <p className={`inline-block text-left font-sans text-sm leading-relaxed px-4 py-2.5 max-w-[90%] ${m.role === "you" ? "bg-cream text-ink" : "bg-white/10 text-cream"}`}>{m.text}</p>
                {m.members?.map((c) => (
                  <Link key={c.slug} to={`/member/${c.slug}`} onClick={() => setOpen(false)}
                    className="mt-2 flex items-center justify-between gap-2 border border-gloss-gold/50 px-4 py-2.5 hover:bg-gloss-gold hover:text-ink transition-colors"
                    data-testid="ask-sme-member-card">
                    <span className="font-serif text-base">{c.business_name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1">{c.category} <ArrowRight size={11} /></span>
                  </Link>
                ))}
              </div>
            ))}
            {busy && <p className="font-mono text-[11px] uppercase tracking-widest text-cream/40 animate-pulse">The desk is thinking…</p>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="flex border-t border-cream/15">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the desk…"
              className="flex-1 bg-transparent px-4 py-3.5 font-sans text-sm focus:outline-none placeholder:text-cream/35"
              data-testid="ask-sme-input" />
            <button type="submit" disabled={busy} className="px-4 text-gloss-gold disabled:opacity-40" data-testid="ask-sme-send">
              <ArrowRight size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
